import React, { useEffect, useMemo, useState } from "react";
import { ModalX } from "../../components";
import { Alert, Button, Checkbox, Input, Option, Radio, Select, Spinner, Tab, TabPanel, Tabs, TabsBody, TabsHeader } from "@material-tailwind/react";
import { AccessibilityIcon, BookTextIcon, ListIcon, ShapesIcon } from "lucide-react";
import { convertToPresentableText, getOrdinal, NotifyError } from "../../utils/functions";
import { ACCOMMODATION_TYPES_ENUM, DESK_TYPES_ENUM, OPERATIONAL_STATUSES_ENUM } from "../../utils/enums";
import { useAddSeat, useUpdateSeat } from "../../custom-hooks/use-seats";
import { useGetCoworkingSpaces } from "../../custom-hooks/use-coworking-spaces";

const INITIAL_FORM_DATA = {
  // Basic Information
  seatCode: '',
  coworking: '',
  // Physical Attributes
  seatType: '',
  // Seat Features
  hasMonitorArm: false,
  hasKeyboard: false,
  hasMouse: false,
  hasErgonomicChair: false,
  hasFootRest: false,
  hasPrivacyScreen: false,
  hasUSBPorts: false,
  powerOutlets: 2,
  hasWiredInternet: true,
  dedicatedInternet: false,
  dedicatedClimateControl: false,
  internetSpeed: 250, // in Mbps
  deskType: '',
  // Accessibility Features
  isWheelchairAccessible: false,
  hasAdjustableHeight: false,
  hasSpecialLighting: false,
  requiresSpecialAccommodation: false,
  accommodationType: [],
  // Status and Availability
  status: 'available',
  isActive: true,
  needsMaintenance: false,
  underMaintenance: false,
  lastMaintenanceDate: '',
  nextMaintenanceDate: '',
  maintenanceNotes: '',
  // Usage and Assignment
  currentAssignmentType: '',
  currentAssignmentUser: '',
  currentAssignmentStartDate: '',
  currentAssignmentEndDate: '',
  // Booking Rules
  isBookable: true,
  naturalLight: false,
  noiseLevel: 'medium',
  nearWindow: false,
  ventilation: '',
  temperature: '',
};

const addModalTabs = [
  {
    label: 'General',
    value: 'general',
    icon: <BookTextIcon size={12} />,
  },
  {
    label: 'Features',
    value: 'features',
    icon: <ListIcon size={12} />,
  },
  {
    label: 'Equipment',
    value: 'equipment',
    icon: <ShapesIcon size={12} />,
  },
  {
    label: 'Accessibility',
    value: 'accessiblity',
    icon: <AccessibilityIcon size={12} />,
  },
];

/**
 * Self-contained create/update modal for seats.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} [props.seat] - null/undefined for create, seat object for edit
 * @param {string} [props.defaultSpaceId] - optional space id to preselect (and lock) on create
 * @param {string} [props.defaultFloorId] - optional floor id to filter spaces on create
 * @param {() => void} [props.onSuccess] - called after successful add/update
 */
const SeatCrudModal = ({
  isOpen,
  onClose,
  seat = null,
  defaultSpaceId,
  defaultFloorId,
  onSuccess,
}) => {
  const isUpdating = Boolean(seat);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const addSeatMutation = useAddSeat();
  const updateSeatMutation = useUpdateSeat();
  const { data: spaces, isLoading: isSpacesLoading, isError: isSpacesError } = useGetCoworkingSpaces();

  const spaceLocked = Boolean(defaultSpaceId) && !isUpdating;

  const filteredSpaces = useMemo(() => {
    if (!spaces) return [];
    if (!defaultFloorId || isUpdating) return spaces;
    return spaces.filter((space) => {
      const floorId = space.floor?._id || space.floor;
      return floorId === defaultFloorId;
    });
  }, [spaces, defaultFloorId, isUpdating]);

  useEffect(() => {
    if (!isOpen) return;

    if (seat) {
      setFormData({ ...seat, coworking: seat.coworking?._id || seat.coworking || '' });
    } else {
      const defaults = { ...INITIAL_FORM_DATA };
      defaults.status = 'operational';
      defaults.isActive = true;
      defaults.seatType = 'common';
      defaults.deskType = 'fixed';
      defaults.ventilation = 'ac';
      defaults.temperature = '26';
      if (defaultSpaceId) {
        defaults.coworking = defaultSpaceId;
      }
      setFormData(defaults);
    }
  }, [isOpen, seat, defaultSpaceId]);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBoxChange = async (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleClose = () => {
    setFormData(INITIAL_FORM_DATA);
    onClose();
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isUpdating) {
        const updatedSeat = {
          seatCode: formData.seatCode,
          seatType: formData.seatType,
          coworking: formData.coworking,
          hasMonitorArm: formData.hasMonitorArm,
          hasKeyboard: formData.hasKeyboard,
          hasMouse: formData.hasMouse,
          hasErgonomicChair: formData.hasErgonomicChair,
          hasFootRest: formData.hasFootRest,
          hasPrivacyScreen: formData.hasPrivacyScreen,
          hasUSBPorts: formData.hasUSBPorts,
          powerOutlets: formData.powerOutlets,
          hasWiredInternet: formData.hasWiredInternet,
          dedicatedInternet: formData.dedicatedInternet,
          dedicatedClimateControl: formData.dedicatedClimateControl,
          internetSpeed: formData.internetSpeed,
          deskType: formData.deskType,
          isWheelchairAccessible: formData.isWheelchairAccessible,
          hasAdjustableHeight: formData.hasAdjustableHeight,
          hasSpecialLighting: formData.hasSpecialLighting,
          requiresSpecialAccommodation: formData.requiresSpecialAccommodation,
          accommodationType: formData.accommodationType,
          status: formData.status,
          isActive: formData.isActive,
          needsMaintenance: formData.needsMaintenance,
          underMaintenance: formData.underMaintenance,
          lastMaintenanceDate: formData.lastMaintenanceDate,
          nextMaintenanceDate: formData.nextMaintenanceDate,
          maintenanceNotes: formData.maintenanceNotes,
          isBookable: formData.isBookable,
          naturalLight: formData.naturalLight,
          noiseLevel: formData.noiseLevel,
          nearWindow: formData.nearWindow,
          ventilation: formData.ventilation,
          temperature: formData.temperature,
        };
        await updateSeatMutation.mutateAsync({ seatId: seat._id, data: updatedSeat });
      } else {
        const newSeat = {
          seatCode: formData.seatCode,
          seatType: formData.seatType,
          hasMonitorArm: formData.hasMonitorArm,
          hasKeyboard: formData.hasKeyboard,
          hasMouse: formData.hasMouse,
          hasErgonomicChair: formData.hasErgonomicChair,
          hasFootRest: formData.hasFootRest,
          hasPrivacyScreen: formData.hasPrivacyScreen,
          hasUSBPorts: formData.hasUSBPorts,
          powerOutlets: formData.powerOutlets,
          hasWiredInternet: formData.hasWiredInternet,
          dedicatedInternet: formData.dedicatedInternet,
          dedicatedClimateControl: formData.dedicatedClimateControl,
          internetSpeed: formData.internetSpeed,
          deskType: formData.deskType,
          isWheelchairAccessible: formData.isWheelchairAccessible,
          hasAdjustableHeight: formData.hasAdjustableHeight,
          hasSpecialLighting: formData.hasSpecialLighting,
          requiresSpecialAccommodation: formData.requiresSpecialAccommodation,
          accommodationType: formData.accommodationType,
          status: formData.status,
          isActive: formData.isActive,
          needsMaintenance: formData.needsMaintenance,
          underMaintenance: formData.underMaintenance,
          lastMaintenanceDate: formData.lastMaintenanceDate,
          nextMaintenanceDate: formData.nextMaintenanceDate,
          maintenanceNotes: formData.maintenanceNotes,
          isBookable: formData.isBookable,
          naturalLight: formData.naturalLight,
          noiseLevel: formData.noiseLevel,
          nearWindow: formData.nearWindow,
          ventilation: formData.ventilation,
          temperature: formData.temperature,
        };
        await addSeatMutation.mutateAsync({ spaceId: formData.coworking, data: newSeat });
      }

      onSuccess?.();
    } catch (error) {
      NotifyError(error.response.data.message, error);
    } finally {
      setFormData(INITIAL_FORM_DATA);
      onClose();
    }
  };

  return (
    <ModalX
      isOpen={isOpen}
      onClose={handleClose}
      title={isUpdating ? "Update Seat" : "Add New Seat"}
      maxWidth="xxl"
    >
      <div className={`flex gap-5 flex-col md:flex-row`}>
        <div className={`w-full `}>
          <form onSubmit={handleFormSubmit}>
            <div className="space-y-2">
              <div className="pb-3">
                <Tabs value="general">
                  <TabsHeader className="rounded-full" indicatorProps={{ className: 'rounded-full' }}>
                    {addModalTabs.map((tab, index) => (
                      <Tab key={index} className="rounded-full font-thin text-xs" value={tab.value}>
                        <span className={`hidden md:block`}>{tab.label}</span>
                        <span className={`block md:hidden`}>{tab.icon}</span>
                      </Tab>
                    ))}
                  </TabsHeader>
                  <TabsBody className="min-h-[500px]">
                    <TabPanel value={addModalTabs[0].value} className="space-y-4">
                      {isSpacesError && <div className="text-center py-5 text-red-500">Error loading spaces. Please try again.</div>}
                      <div className={`flex gap-4 flex-col md:flex-row`}>
                        <Input
                          label="Seat Code"
                          name="seatCode"
                          value={formData.seatCode}
                          onChange={handleInputChange}
                          required
                        />
                        <Select
                          label="Status"
                          value={formData.status}
                          onChange={newValue => handleSelectChange("status", newValue)}
                        >
                          {OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                            <Option key={index} value={status}>
                              {convertToPresentableText(status)}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      {isSpacesLoading ? <Spinner /> :
                        isSpacesError ? <div className="text-center py-5 text-red-500">Error loading spaces. Please try again.</div> :
                          <Select
                            label={'Select Space'}
                            value={formData.coworking}
                            onChange={(newValue) => handleSelectChange("coworking", newValue)}
                            disabled={spaceLocked}
                          >
                            {filteredSpaces?.map((space, index) => (
                              <Option key={index} value={space._id}>
                                <div className="flex justify-between gap-3 w-full">
                                  <div className="">
                                    {getOrdinal(space.number)} - {space?.code || 'N/A'}
                                  </div>
                                  <div className="">
                                    {space.floor.building.name} - {space.floor.building.branch.name}
                                  </div>
                                </div>
                              </Option>
                            ))}
                          </Select>
                      }

                      <div className={`flex gap-4 flex-col md:flex-row`}>
                        <Select
                          label="Seat Type"
                          value={formData.seatType}
                          onChange={newValue => handleSelectChange("seatType", newValue)}
                        >
                          {ACCOMMODATION_TYPES_ENUM.map((type, index) => (
                            <Option key={index} value={type}>
                              {convertToPresentableText(type)}
                            </Option>
                          ))}
                        </Select>
                        <Select
                          label="Desk Type"
                          value={formData.deskType}
                          onChange={newValue => handleSelectChange("deskType", newValue)}
                        >
                          {DESK_TYPES_ENUM.map((type, index) => (
                            <Option key={index} value={type}>
                              {convertToPresentableText(type)}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <div className={`flex flex-col`}>
                        <Checkbox
                          name="naturalLight"
                          checked={formData.naturalLight}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Has Natural Light"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="nearWindow"
                          checked={formData.nearWindow}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Adjacent Window"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="dedicatedInternet"
                          checked={formData.dedicatedInternet}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Dedicated Internet"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="dedicatedClimateControl"
                          checked={formData.dedicatedClimateControl}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Dedicated Climate Control"
                          onChange={handleCheckBoxChange}
                        />
                        <hr />
                        <div className={`flex flex-col md:flex-row md:items-center mt-4`}>
                          <p>Ventilation</p>
                          <div className="flex">
                            <Radio name="ventilation" value={formData.ventilation} label="Natural" onChange={() => handleSelectChange("ventilation", "natural")} />
                            <Radio name="ventilation" value={formData.ventilation} label="AC" defaultChecked onChange={() => handleSelectChange("ventilation", "ac")} />
                            <Radio name="ventilation" value={formData.ventilation} label="Both" onChange={() => handleSelectChange("ventilation", "both")} />
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                    <TabPanel value={addModalTabs[1].value} className="space-y-4 flex flex-col md:flex-row gap-4">
                      <div className={`flex flex-col`}>
                        <Checkbox
                          name="hasMonitorArm"
                          checked={formData.hasMonitorArm}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Has Monitor Arm"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="hasKeyboard"
                          checked={formData.hasKeyboard}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Dedicated Keyboard"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="hasMouse"
                          checked={formData.hasMouse}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Dedicated Mouse"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="hasErgonomicChair"
                          checked={formData.hasErgonomicChair}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Ergonomic Chair"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="hasFootRest"
                          checked={formData.hasFootRest}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Foot Rest"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="hasPrivacyScreen"
                          checked={formData.hasPrivacyScreen}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Privacy Partition"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="hasWiredInternet"
                          checked={formData.hasWiredInternet}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Wired Internet"
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          name="hasUSBPorts"
                          checked={formData.hasUSBPorts}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          label="Has USB Ports"
                          onChange={handleCheckBoxChange}
                        />
                      </div>
                      <div className={`flex flex-col gap-4`}>
                        <Select
                          label="Power Outlets"
                          value={formData.powerOutlets}
                          onChange={newValue => handleSelectChange("powerOutlets", newValue)}
                        >
                          {[0, 1, 2, 3, 4, 5, 6].map((x, i) => (<Option key={i} value={x}>{x}</Option>))}
                        </Select>
                        <Input
                          label="Internet Speed"
                          name="internetSpeed"
                          value={formData.internetSpeed}
                          onChange={handleInputChange}
                        />
                        <Input
                          label="Average Temperature"
                          name="temperature"
                          value={formData.temperature}
                          onChange={handleInputChange}
                        />
                      </div>
                    </TabPanel>
                    <TabPanel value={addModalTabs[2].value} className="space-y-4 flex flex-col gap-4">
                      <Alert variant="outlined">
                        <span className="font-thin text-xs">
                          This Functionality will be available soon.
                        </span>
                      </Alert>
                      <div className={`flex justify-between items-center`}>
                        <div className={``}>
                          <span className="font-normal text-md">Provided Equipment List</span>
                          <p className="font-thin text-xs">Add the items provided to this seat</p>
                        </div>
                        <Button
                          className="rounded-full"
                          size="sm"
                          disabled
                        >Add</Button>
                      </div>
                      {[1].map((eq, i) => (
                        <div key={i} className="flex gap-4">
                          <Input
                            label={`Item / Model`}
                            name={`eq_${i + 1}`}
                            value={eq}
                            onChange={e => handleInputChange(`eq_${i + 1}`, e.target.value)}
                            disabled
                          />
                          <Input
                            label="Serial #"
                            name={`eq_type_${i + 1}`}
                            value={formData[`eq_type_${i + 1}`]}
                            onChange={e => handleInputChange(`eq_type_${i + 1}`, e.target.value)}
                            disabled
                          />
                          <Input
                            label="Last Maintainence"
                            name={`eq_type_${i + 1}`}
                            type="date"
                            value={formData[`eq_type_${i + 1}`]}
                            onChange={e => handleInputChange(`eq_type_${i + 1}`, e.target.value)}
                            disabled
                          />
                        </div>
                      ))}
                    </TabPanel>
                    <TabPanel value={addModalTabs[3].value} className="">
                      <div className={`flex flex-col`}>
                        <Checkbox
                          label="Seat Accessible For Wheel Chair"
                          name="isWheelchairAccessible"
                          checked={formData.isWheelchairAccessible}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          label="Provided Seat has Adjustable Height"
                          name="hasAdjustableHeight"
                          checked={formData.hasAdjustableHeight}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          label="Has Special Dedicated Lighting"
                          name="hasSpecialLighting"
                          checked={formData.hasSpecialLighting}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                          label="Special Accomodation"
                          name="requiresSpecialAccommodation"
                          checked={formData.requiresSpecialAccommodation}
                          className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                          onChange={handleCheckBoxChange}
                        />
                      </div>
                    </TabPanel>
                  </TabsBody>
                </Tabs>
              </div>
            </div>
            <hr />
            <div className="mt-4 flex justify-end space-x-2">
              <Button
                onClick={handleClose}
                className="rounded-full "
                variant="text"
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-full">
                {isUpdating ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </div>
        <div className={`w-full hidden border rounded-3xl p-4`}>
          summary
        </div>
      </div>
    </ModalX>
  );
};

export default SeatCrudModal;
export { SeatCrudModal, INITIAL_FORM_DATA };
